import type {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse,
	Holding,
	TradingAccountMargin,
	GetTradingAccountMarginRequest
} from '../proto/generated/trading-bridge'
import type { MarginFromBrokerRequest, MarginFromBrokerResponse, Order } from '../proto/generated/order'
import type { Position, PositionRequest, PositionResponse } from '../proto/generated/position'
import type { Exchange } from '../proto/generated/common'
import { Security } from '../proto/generated/security'
import {
	AuthProvider,
	AuthProviderLoginUrlResponse,
	ForgetPasswordRequest,
	GetActiveSessionsResponse,
	LoginRequest,
	LoginResponse,
	LoginWithMobileRequest,
	LoginWithMobileResponse,
	OperationResponse,
	User,
	UserSignUpRequest,
	ValidateThirdPartyTokenRequest,
	VerifyMobileLoginRequest
} from '../proto/generated/identity'

/**
 * Backend provider id, as set by VITE_BACKEND_PROVIDER. Providers register themselves under their id
 * (see providers/registry); tick2trade is the built-in one.
 */
export type BackendProvider = string
export const TICK2TRADE_PROVIDER: BackendProvider = 'tick2trade'

/**
 * Feed kinds a provider (broker) websocket can stream on request.
 * Keys are provider-specific opaque strings (whatever the provider uses to address an instrument).
 */
export type ProviderWebSocketFeedKind = 'touchline' | 'depth'

/** Account-level streams that need an explicit subscribe frame after connect. */
export type ProviderWebSocketStream = 'orders' | 'positions'

/**
 * Per-mode configuration for an *extra* provider websocket that lives next to the
 * tick2trade backend websocket. `enabled` is the master switch:
 *   - tick2trade mode: false (the backend websocket already streams everything)
 *   - broker providers: true (the broker's own websocket for order/position/market feeds)
 */
export interface ProviderWebSocketConfig {
	enabled: boolean
	url: string
	/** Must match the `source` used at login (WEB / MOB). */
	source: string
	heartbeatIntervalMs: number
	reconnectDelayMs: number
	maxReconnectDelayMs: number
	features: {
		/** Push broker order updates into the orders store. */
		orderUpdates: boolean
		/** Push broker position updates into the positions store. */
		positionUpdates: boolean
		/** Allow touchline/depth subscriptions through this socket. */
		marketData: boolean
	}
}

export const DISABLED_PROVIDER_WEBSOCKET_CONFIG: ProviderWebSocketConfig = {
	enabled: false,
	url: '',
	source: 'WEB',
	heartbeatIntervalMs: 30000,
	reconnectDelayMs: 2000,
	maxReconnectDelayMs: 30000,
	features: {
		orderUpdates: false,
		positionUpdates: false,
		marketData: false
	}
}

export interface ProviderSecurityRef {
	exchange: Exchange
	code: string
}

/** Normalised quote fields (prices in provider units, i.e. rupees). */
export interface ProviderQuote {
	lastTradedPrice?: number
	percentChange?: number
	volume?: number
	open?: number
	high?: number
	low?: number
	close?: number
	averageTradePrice?: number
	openInterest?: number
	previousOpenInterest?: number
	totalOpenInterest?: number
	lastTradeTime?: string
	lastTradeQuantity?: number
	totalBuyQuantity?: number
	totalSellQuantity?: number
	lowerCircuit?: number
	upperCircuit?: number
	high52Week?: number
	low52Week?: number
	feedTime?: string
	tickSize?: number
	lotSize?: number
	pricePrecision?: number
	tradingSymbol?: string
}

export interface ProviderDepthLevel {
	price: number
	quantity: number
	orders?: number
}

export type ProviderWebSocketEvent =
	| { type: 'connected'; uid?: string }
	| { type: 'connect_rejected'; reason: string }
	| { type: 'heartbeat_ack'; feedTime?: string }
	| { type: 'subscription_ack'; kind: ProviderWebSocketFeedKind; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; raw: unknown }
	| { type: 'unsubscription_ack'; kind: ProviderWebSocketFeedKind; keys: string[] }
	| { type: 'stream_ack'; stream: ProviderWebSocketStream; subscribed: boolean; raw: unknown }
	| { type: 'touchline'; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; bids: ProviderDepthLevel[]; asks: ProviderDepthLevel[]; raw: unknown }
	| { type: 'depth'; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; bids: ProviderDepthLevel[]; asks: ProviderDepthLevel[]; raw: unknown }
	| { type: 'order_update'; order: Order | null; reportType?: string; raw: unknown }
	| { type: 'position_update'; position: Position | null; raw: unknown }
	| { type: 'alert'; message: string; raw: unknown }
	| { type: 'admin_message'; message: string; messageType?: string; timestamp?: string; raw: unknown }
	| { type: 'market_status'; exchange: string; status: string; marketType: string; description: string; timestamp?: string; raw: unknown }
	| { type: 'unknown'; raw: unknown }

/**
 * Wire-protocol strategy for the provider websocket. Each backend that needs an extra
 * socket implements this; the React layer stays provider-agnostic.
 * Any `build*` method returns null when the message cannot be produced yet
 * (e.g. the session token or user id is not available).
 */
export interface IProviderWebSocketProtocol {
	buildConnectMessage(): string | null
	buildHeartbeatMessage(): string | null
	buildSubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null
	buildUnsubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null
	/** Subscribe / unsubscribe an account stream (order updates, position updates). */
	buildSubscribeStreamMessage(stream: ProviderWebSocketStream): string | null
	buildUnsubscribeStreamMessage(stream: ProviderWebSocketStream): string | null
	/** Provider-specific subscription key for a security, or null if it cannot be derived. */
	getSubscriptionKey(security: ProviderSecurityRef): string | null
	parseMessage(raw: string): ProviderWebSocketEvent[]
}

/**
 * What the UI may offer on a backend. The provider's config declares it; UI code branches on these
 * flags and never on the provider's name.
 */
export interface ProviderCapabilities {
	/** Trading accounts are a directory the user manages: list brokers, connect, pick a primary. Off: the single broker session is the account. */
	tradingAccountDirectory: boolean
	/** Per-account margins / limits can be fetched from the backend. */
	accountMargins: boolean
	/** The broker session is a provider token in storage, and a valid one is enough to trade (no ACTIVE account record needed). */
	brokerSessionToken: boolean
	/** Holdings belong to each trading account (refetch on account switch). Off: one holdings set per broker session. */
	holdingsPerAccount: boolean
	/** A practice (paper) trading account is offered. */
	practiceTrade: boolean
	/** Account profile / demat details come from the backend. */
	accountProfile: boolean
	/** The Reports section is available. */
	reports: boolean
	/** The AI assistant is available. */
	aiAssistant: boolean
	/** The app refreshes the access token itself. Off when the provider's OAuth mints it and a failed refresh must not log the user out. */
	refreshesAccessToken: boolean
	/** Only these broker codes can be connected; unset = any. */
	connectableBrokerCodes?: string[]
	/** Trading accounts with these broker codes are hidden; unset = none hidden. */
	hiddenBrokerCodes?: string[]
}

/**
 * Sign-in through the backend provider's own OAuth, for backends whose users come from the provider.
 * The provider sends the user back to `redirectUrl` with its token in the `tokenParam` query parameter;
 * the app exchanges it with the backend (ValidateThirdPartyTokenRequest) for its own session.
 */
export interface ProviderLogin {
	/** AuthProvider value the backend expects for this provider's tokens. */
	authProvider: AuthProvider
	/** Provider key as used by VITE_AUTO_LOGIN_PROVIDER and stored under OAUTH_PROVIDER after login. */
	key: string
	/** Query parameter that carries the provider's token on the redirect back. */
	tokenParam: string
	/** Where the provider sends the user back (VITE_PROVIDER_OAUTH_REDIRECT_URL). Its path is the app route. */
	redirectUrl: string
}

export interface BackendConfig {
	provider: BackendProvider
	capabilities: ProviderCapabilities
	baseUrl: string
	providerUrl?: string
	wsUrl?: string
	apiKey?: string
	headers?: Record<string, string>
	providerWebSocket?: ProviderWebSocketConfig
}

export interface IBackendAdapter {
	getProvider(): BackendProvider
	getCapabilities(): ProviderCapabilities
	getBaseUrl(): string
	getProviderUrl(): string
	getWebSocketUrl(): string
	genrateLocalSecurityMap(security: Security): void
	getProviderWebSocketConfig(): ProviderWebSocketConfig
	getProviderWebSocketProtocol(): IProviderWebSocketProtocol | null

	transformRequest<T = any>(endpoint: string, data: T, method: string): {
		url: string
		data: any
		headers?: Record<string, string>
	}

	transformResponse<T = any>(response: any, endpoint: string): T

	transformError(error: any): any

	placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse>
	getOrders(request: GetAllOrdersRequest): Promise<Order[]>
	cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse>
	getPositions(request: PositionRequest): Promise<PositionResponse>
	getPlacedOrder(params: any): Promise<Order>
	getHoldings(accountId: string | undefined): Promise<Holding[]>
	syncTradingAccountMargin(request: GetTradingAccountMarginRequest): Promise<TradingAccountMargin>
	getRequiredMargin(request: MarginFromBrokerRequest): Promise<MarginFromBrokerResponse>

	getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse>
	loginWithMail(body: LoginRequest): Promise<LoginResponse>
	loginWithMobile(body: LoginWithMobileRequest): Promise<LoginWithMobileResponse>
	verifyMobileLogin(body: VerifyMobileLoginRequest): Promise<LoginResponse>
	signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse>
	sendEmailOtp(email: string, options?: { purpose?: string }): Promise<any>
	forgetPassword(body: ForgetPasswordRequest): Promise<User>
	validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse>

	logout(): void
	getActiveSessions(): Promise<GetActiveSessionsResponse>
	revokeSession(deviceId: string): Promise<OperationResponse>
}
